// src/components/layout/InputArea/InputArea.jsx
import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import * as Tooltip from '@radix-ui/react-tooltip';
import { ArrowRight, Paperclip, Image, Loader, Mic, Trash2, Square } from 'lucide-react';
import { useFileAttachment, isImageFile } from '@/managers/FileAttachmentManager';
import { useConversation } from '@/managers/ConvoManager';
import { useChatFeed } from '@/managers/ChatFeedManager/ChatFeedManager';
import { keyStateManager } from '@/managers/zKeysManager';
import FileAttachmentPreview from './FileAttachmentPreview';
import DragDropZone from '@/components/layout/DragDropZone';
import ChatControlStackComponent from '@/components/tools/ChatControlStack';
import { useTheme } from '@/managers/ThemeProvider';

const ChatControlStack = React.memo(ChatControlStackComponent);

const BASE_TEXTAREA_HEIGHT = 44;
const MAX_TEXTAREA_HEIGHT = 200;
const MAX_RECORDING_TIME = 1200;

const OrbitalSpinner = () => (
    <div className="relative w-4 h-4">
        <div className="absolute w-4 h-4">
            {[1, 2, 3].map((index) => (
                <div
                    key={index}
                    className={`absolute w-[3px] h-[3px] bg-current rounded-full origin-[8px_8px]
                    ${index === 1 ? 'animate-[orbit_1.5s_infinite_linear]' :
                    index === 2 ? 'animate-[orbit_1.5s_infinite_linear_0.5s]' :
                    'animate-[orbit_1.5s_infinite_linear_1s]'}`}
                />
            ))}
        </div>
    </div>
);

const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
};

const usePrevious = (value) => {
    const ref = useRef();
    useEffect(() => {
        ref.current = value;
    });
    return ref.current;
};

const InputArea = ({
    isPortalOpen = false,
    canAttachFiles = true,
    canAttachImages = false,
    chatFontSize,
    currentConversation: parentCurrentConversation,
}) => {
    const { isLightTheme } = useTheme();
    const {
        stagedFiles, addFile, addFiles, removeFile, clearFiles, updateFile, hasUnreadyFiles,
        stageExistingFiles, uploadFiles
    } = useFileAttachment();
    const {
        currentConversationId, isNavigating, rewoundMessagePayload, clearRewoundMessagePayload, currentConversation
    } = useConversation();
    const { sendMessage: chatFeedSendMessage, cancelStream } = useChatFeed();

    const [message, setMessage] = useState('');
    const [keyState, setKeyState] = useState(() => keyStateManager.getState());
    const [isFocused, setIsFocused] = useState(false);
    const [textareaHeight, setTextareaHeight] = useState(BASE_TEXTAREA_HEIGHT);
    const [isRecording, setIsRecording] = useState(false);
    const [recordingTime, setRecordingTime] = useState(0);
    const [recordingError, setRecordingError] = useState(null);
    const [sendError, setSendError] = useState(null);

    const textareaRef = useRef(null);
    const fileInputRef = useRef(null);
    const imageInputRef = useRef(null);
    const mediaRecorderRef = useRef(null);
    const streamRef = useRef(null);
    const chunksRef = useRef([]);
    const timerRef = useRef(null);
    
    const wasUnready = usePrevious(hasUnreadyFiles);

    // ARCHITECTURAL FIX: This effect declaratively restores focus.
    // It runs only when `hasUnreadyFiles` changes from true to false.
    // This is the guaranteed way to run *after* the textarea is re-enabled.
    useEffect(() => {
        if (wasUnready && !hasUnreadyFiles) {
            textareaRef.current?.focus();
        }
    }, [hasUnreadyFiles, wasUnready]);

    useEffect(() => {
        if (textareaRef.current) {
            if (message === '') {
                const newHeight = BASE_TEXTAREA_HEIGHT;
                textareaRef.current.style.height = `${newHeight}px`;
                setTextareaHeight(newHeight);
            } else {
                textareaRef.current.style.height = 'auto';
                const scrollHeight = textareaRef.current.scrollHeight;
                const newHeight = Math.min(Math.max(scrollHeight, BASE_TEXTAREA_HEIGHT), MAX_TEXTAREA_HEIGHT);
                textareaRef.current.style.height = `${newHeight}px`;
                setTextareaHeight(newHeight);
            }
        }
    }, [message]);
    
    useEffect(() => {
        setSendError(null);
    }, [currentConversationId]);

    useEffect(() => {
        if (rewoundMessagePayload) {
                setMessage((rewoundMessagePayload.content || '').trim());

            const allAttachments = [...(rewoundMessagePayload.files || []), ...(rewoundMessagePayload.images || [])];
            if (allAttachments.length > 0) {
                const attachmentsToStage = allAttachments.reduce((acc, attachment) => {
                    acc[attachment.id] = { ...attachment, status: 'ready', preview: isImageFile(attachment) ? attachment.storageUrl : (attachment.content ? attachment.content.substring(0, 200) : null) };
                    return acc;
                }, {});
                stageExistingFiles(attachmentsToStage);
            } else {
                clearFiles();
            }
            clearRewoundMessagePayload();
        }
    }, [rewoundMessagePayload, stageExistingFiles, clearFiles, clearRewoundMessagePayload]);


    const cleanupRecording = useCallback(() => {
        if (timerRef.current) clearInterval(timerRef.current);
        timerRef.current = null;
        if (streamRef.current) streamRef.current.getTracks().forEach(track => track.stop());
        streamRef.current = null;
        if (mediaRecorderRef.current) {
            mediaRecorderRef.current.onstop = null;
            mediaRecorderRef.current.ondataavailable = null;
        }
        mediaRecorderRef.current = null;
        chunksRef.current = [];
    }, []);

    useEffect(() => {
        return cleanupRecording;
    }, [cleanupRecording]);

    useEffect(() => {
        const unsubscribeKeys = keyStateManager.subscribe(setKeyState);

        if (!keyStateManager.isReady()) {
            keyStateManager.refreshFromDB().catch((error) => {
                console.error('[InputArea] Failed to refresh key state:', error);
            });
        }

        return unsubscribeKeys;
    }, []);

    const handleMessageChange = useCallback((e) => {
        if (!e?.target || isRecording) return;
        setMessage(e.target.value);
        setSendError(null);
    }, [isRecording]);

    const stopRecording = useCallback(() => {
        if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
            mediaRecorderRef.current.stop();
        }
        setIsRecording(false);
        if (timerRef.current) clearInterval(timerRef.current);
        timerRef.current = null;
    }, []);

    const startRecording = useCallback(async () => {
        try {
            setRecordingError(null);
            const stream = await navigator.mediaDevices.getUserMedia({ audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true } });
            streamRef.current = stream;
            chunksRef.current = [];
            const mimeType = MediaRecorder.isTypeSupported('audio/mp3') ? 'audio/mp3' : 'audio/webm';
            const mediaRecorder = new MediaRecorder(stream, { mimeType, audioBitsPerSecond: 128000 });
            mediaRecorderRef.current = mediaRecorder;

            mediaRecorder.ondataavailable = (event) => { if (event.data.size > 0) chunksRef.current.push(event.data); };
            mediaRecorder.onstop = async () => {
                const blob = new Blob(chunksRef.current, { type: mimeType });
                setRecordingTime(0);
                if (blob.size > 0) {
                    const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
                    const fileExtension = mimeType.includes('webm') ? 'webm' : 'mp3';
                    const fileName = `voice_message_${timestamp}.${fileExtension}`;
                    try { await addFile(blob, fileName, mimeType); }
                    catch (error) { setRecordingError('Failed to stage voice message'); }
                    finally { cleanupRecording(); }
                } else { setRecordingError("Recording was empty."); cleanupRecording(); }
            };
            mediaRecorder.start(1000);
            setIsRecording(true); setRecordingTime(0);
            if(timerRef.current) clearInterval(timerRef.current);
            timerRef.current = setInterval(() => {
                setRecordingTime(prev => {
                    const newTime = prev + 1;
                    if (newTime >= MAX_RECORDING_TIME) { stopRecording(); return MAX_RECORDING_TIME; }
                    return newTime;
                });
            }, 1000);
        } catch (err) { setRecordingError('Mic access denied or error.'); cleanupRecording(); setIsRecording(false); }
    }, [addFile, cleanupRecording, stopRecording]);

    const cancelRecording = useCallback(() => {
        if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
            mediaRecorderRef.current.onstop = () => { cleanupRecording(); setIsRecording(false); setRecordingTime(0); setRecordingError(null); };
            mediaRecorderRef.current.stop();
        } else { cleanupRecording(); setIsRecording(false); setRecordingTime(0); setRecordingError(null); }
    }, [cleanupRecording]);

    const handlePaste = useCallback(async (e) => {
        if (isRecording) { e.preventDefault(); return; }
        const clipboardData = e.clipboardData;
        const text = clipboardData.getData('text/plain');
        const items = Array.from(clipboardData.items);
        const fileItems = items.filter(item => item.kind === 'file').map(item => item.getAsFile()).filter(Boolean);
        
        if (text && text.length > 300 && canAttachFiles) {
            e.preventDefault();
            const blob = new Blob([text], { type: 'text/plain' });
            await addFile(blob, 'Pasted Clipboard.txt', 'text/plain', true);
            // No longer need to call .focus() here; the effect will handle it.
            return;
        }
        if (fileItems.length > 0) {
            e.preventDefault();
            const validFiles = fileItems.filter(file => !(isImageFile(file) && !canAttachImages));
            if (validFiles.length > 0) {
                await addFiles(validFiles, true);
                // No longer need to call .focus() here; the effect will handle it.
            }
        }
    }, [isRecording, canAttachFiles, canAttachImages, addFile, addFiles]);

    const handleDrop = useCallback(async (files) => {
        if (files && files.length > 0) {
            await addFiles(files);
            // No longer need to call .focus() here; the effect will handle it.
        }
    }, [addFiles]);

    const isConversationProcessing = useMemo(() => {
        return currentConversation?.status === 'inflight';
    }, [currentConversation?.status]);

    const hasGeminiKey = useMemo(() => Boolean(keyState.keys?.GEMINI), [keyState.keys]);

    const isMicBlockedForGemini = useMemo(() => {
        return !isRecording && keyState.isKeysReady && !hasGeminiKey;
    }, [hasGeminiKey, isRecording, keyState.isKeysReady]);

    const isMicDisabled = useMemo(() => {
        return hasUnreadyFiles || isNavigating || !currentConversationId || isMicBlockedForGemini;
    }, [currentConversationId, hasUnreadyFiles, isMicBlockedForGemini, isNavigating]);

    const canSend = useMemo(() => {
        const hasText = message.trim().length > 0;
        const hasFiles = Object.keys(stagedFiles).length > 0;
        return (hasText || hasFiles) && !isConversationProcessing && !hasUnreadyFiles && !isNavigating && currentConversationId;
    }, [message, stagedFiles, isConversationProcessing, hasUnreadyFiles, isNavigating, currentConversationId]);
    
    const handleSubmit = useCallback(async (e) => {
        e?.preventDefault();
        if (!canSend) return;
    
        const allStagedFiles = Object.values(stagedFiles);
        const imageFilesToUpload = allStagedFiles.filter(f => isImageFile(f));
        const otherFilesToSend = allStagedFiles.filter(f => !isImageFile(f));
    
        const filesForServerUpload = otherFilesToSend
            .filter(f => f.fileBlob) // FIX: Ensure we only try to upload files that have a blob.
            .map(file => ({
                blob: file.fileBlob,
                originalname: file.name,
            }));

        const fullMessageContent = message.trim();
    
        if (!fullMessageContent && allStagedFiles.length === 0) return;
    
        try {
            setSendError(null);
    
            const uploadedImageResults = await uploadFiles(imageFilesToUpload.reduce((acc, file) => {
                acc[file.id] = file;
                return acc;
            }, {}));
    
            const uploadErrors = uploadedImageResults.filter(f => f.status === 'error');
            if (uploadErrors.length > 0) {
                setSendError(`Failed to upload ${uploadErrors.length} image(s). Please remove them and try again.`);
                uploadErrors.forEach(errFile => updateFile(errFile.id, { status: 'error', error: 'Upload Failed' }));
                return;
            }
    
            const hasAudio = otherFilesToSend.some(file => file.type?.startsWith('audio/'));
    
            const messagePayload = {
                rawContent: fullMessageContent,
                contentBlocks: [],
                hasAudio,
                files: {},
                currentMediaItems: [],
            };
    
            if (fullMessageContent) {
                messagePayload.contentBlocks.push({ type: 'text', content: fullMessageContent });
            }
    
            uploadedImageResults.forEach(file => {
                messagePayload.files[file.id] = { id: file.id, name: file.name, type: file.type, size: file.size };
                messagePayload.currentMediaItems.push({
                    fileName: file.name,
                    name: file.name,
                    mimeType: file.type,
                    type: file.type,
                    id: file.id,
                    size: file.size,
                    url: file.storageUrl,
                    storageUrl: file.storageUrl,
                });
                if (file.storageUrl) messagePayload.files[file.id].storageUrl = file.storageUrl;
            });
    
            otherFilesToSend.forEach(file => {
                messagePayload.files[file.id] = { id: file.id, name: file.name, type: file.type, size: file.size, content: file.content || '', contentWithLines: file.contentWithLines || '' };
            });
    
            // --- FIRE AND FORGET ---
            // Kick off the send process but don't wait for it to complete to clear the UI.
            // The ChatFeedManager is responsible for handling the async state.
            chatFeedSendMessage(messagePayload, filesForServerUpload, {})
                .catch(error => {
                    // Even if the send fails, we've already cleared the UI optimistically.
                    // The error will be handled and displayed at the message level by ChatFeedManager.
                    console.error("[InputArea] Send message promise rejected:", error);
                    // Optionally set a transient error state here if needed, but the primary error
                    // should be attached to the optimistic message itself.
                    setSendError(error.message || "Failed to send. See message status for details.");
                });
    
            // --- CLEAR UI IMMEDIATELY ---
            setMessage('');
            clearFiles();
            if (isRecording) stopRecording();
    
        } catch (error) {
            // This will only catch synchronous errors during payload preparation.
            console.error('[InputArea] Synchronous error during handleSubmit:', error);
            setSendError(error.message || 'An unexpected error occurred');
        }
    }, [message, stagedFiles, canSend, isRecording, uploadFiles, stopRecording, clearFiles, chatFeedSendMessage, updateFile]);

    const handleFileSelect = useCallback(async (e) => {
        const selectedFiles = [...e.target.files];
        e.target.value = ''; // Allow re-selecting the same file
        
        const validFiles = selectedFiles.filter(file => !(isImageFile(file) && !canAttachImages));

        if (validFiles.length > 0) {
            await addFiles(validFiles);
        }
    }, [addFiles, canAttachImages]);

    const triggerFileUpload = useCallback((type) => {
        if (type === 'image' && !canAttachImages) return;
        if (type === 'file' && !canAttachFiles) return;
        if (type === 'image') imageInputRef.current?.click();
        else fileInputRef.current?.click();
    }, [canAttachImages, canAttachFiles]);

    const handleKeyDown = useCallback((e) => {
        if (isRecording) {
            if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); stopRecording(); setTimeout(() => { handleSubmit(e).catch(console.error); }, 200); }
            else if (e.key === 'Escape') { e.preventDefault(); cancelRecording(); }
            return;
        }

        if (e.key === 'Enter' && !e.shiftKey && canSend) { e.preventDefault(); handleSubmit(e); }
    }, [handleSubmit, canSend, isRecording, stopRecording, cancelRecording]);

    const showImagePlaceholder = useMemo(() => {
        const activeTools = currentConversation?.lastUsedMessageSettings?.activeToolIds;
        return Array.isArray(activeTools) && activeTools.includes('image_generation');
    }, [currentConversation?.lastUsedMessageSettings?.activeToolIds]);

    const getPlaceholderText = () => {
        if (isRecording) return `🎤 Recording... ${formatTime(recordingTime)} (Enter to send, Esc to cancel)`;
        return showImagePlaceholder ? "Describe the image you would like..." : "Message...";
    };

    const handleCancelSend = useCallback(() => {
        if (!isConversationProcessing || !currentConversation) return;
        if (currentConversation.status === 'inflight') {
            cancelStream();
        }
    }, [isConversationProcessing, currentConversation, cancelStream]);
    
    const isTextareaTall = textareaHeight > BASE_TEXTAREA_HEIGHT;

    const containerClasses = `relative transition-all duration-300 ease-in-out ${isPortalOpen ? 'w-full' : 'w-full max-w-3xl mx-auto rounded-2xl mb-2'} ${isLightTheme ? 'bg-surface border-t border-border-color shadow-[0_0_20px_rgba(0,0,0,0.08)]' : 'bg-surface border-t border-border-color'}`;
    const inputContainerClasses = `flex-1 flex rounded-xl border transition-all duration-200 overflow-hidden min-h-[${BASE_TEXTAREA_HEIGHT}px] ${isFocused ? 'shadow-input-focus border-border-color' : 'border-border-color'} ${isRecording ? 'border-primary/50 bg-primary/10' : ''} ${recordingError ? 'border-error/50 bg-error/10' : ''} ${sendError ? 'border-error/50 bg-error/10' : ''} ${isNavigating ? 'opacity-50' : ''} flex-row items-stretch`;
    const textareaClasses = `flex-1 resize-none outline-none px-4 py-2.5 transition-all duration-300 text-on-surface text-${chatFontSize} leading-normal placeholder:text-on-surface-dim bg-surface-dark ${isRecording || isNavigating || hasUnreadyFiles ? 'cursor-not-allowed' : ''}`;
    
    const buttonContainerClasses = `flex pl-2 pr-2 border-l self-stretch border-border-color ${isTextareaTall ? 'flex-col justify-center gap-2 py-2' : 'items-center'}`;
    
    const sendButtonClasses = `flex items-center justify-center h-11 px-4 rounded-xl text-white transition-all duration-200 ${canSend && !isConversationProcessing ? 'bg-primary hover:bg-primary-dark opacity-100' : 'opacity-50 cursor-not-allowed bg-primary'} ${isRecording ? 'hidden' : ''}`;
    const micButtonClasses = `p-2 transition-colors disabled:opacity-50 rounded-lg ${isRecording ? 'text-red-500 hover:bg-red-500/10 animate-pulse' : isMicBlockedForGemini ? 'text-on-surface-dim/50' : 'text-on-surface-dim hover:text-on-surface hover:bg-surface/20'}`;
    const cancelRecordingButtonClasses = `p-2 text-on-surface-dim hover:text-error hover:bg-error/10 rounded-lg transition-colors`;
    
    return (
        <>
            <DragDropZone onDrop={handleDrop}/>
            <div className={containerClasses}>
                <div className="absolute -top-[70px] right-3 z-50"><ChatControlStackComponent /></div>
                <FileAttachmentPreview files={stagedFiles} onRemoveFile={removeFile} onUpdateFile={updateFile} />
                <input ref={fileInputRef} type="file" multiple className="hidden" onChange={handleFileSelect} accept="*/*" />
                <input ref={imageInputRef} type="file" multiple className="hidden" onChange={handleFileSelect} accept="image/*" />

                <div className="flex items-end w-full">
                    <div className={`flex items-end w-full gap-2 p-3 ${isPortalOpen ? '' : 'px-4'}`}>
                        <div className={inputContainerClasses}>
                            <div className="flex w-full items-stretch flex-1">
                                <textarea
                                    ref={textareaRef} value={isRecording ? '' : message} onChange={handleMessageChange}
                                    onKeyDown={handleKeyDown} onFocus={() => setIsFocused(true)} onBlur={() => setIsFocused(false)}
                                    onPaste={handlePaste} placeholder={getPlaceholderText()} className={textareaClasses}
                                    disabled={hasUnreadyFiles || isNavigating || !currentConversationId}
                                    style={{ height: `${textareaHeight}px`, maxHeight: `${MAX_TEXTAREA_HEIGHT}px` }}
                                    rows={1} readOnly={isRecording}
                                />
                                <div className={buttonContainerClasses}>
                                    <Tooltip.Provider delayDuration={200}>
                                        <Tooltip.Root open={isMicBlockedForGemini ? undefined : false}>
                                            <Tooltip.Trigger asChild>
                                                <span className="inline-flex">
                                                    <button onClick={isRecording ? stopRecording : startRecording} className={micButtonClasses} aria-label={isMicBlockedForGemini ? "Requires Gemini Key for audio transcription" : (isRecording ? "Stop recording" : "Start voice recording")} disabled={isMicDisabled}><Mic className="w-4 h-4" /></button>
                                                </span>
                                            </Tooltip.Trigger>
                                            {isMicBlockedForGemini && (
                                                <Tooltip.Portal>
                                                    <Tooltip.Content
                                                        side="top"
                                                        sideOffset={8}
                                                        className={`z-[9999] rounded-lg border border-border-color px-3 py-2 text-xs shadow-lg ${isLightTheme ? 'bg-white text-slate-700' : 'bg-surface-dark text-on-surface'}`}
                                                    >
                                                        <div className="text-center leading-tight">
                                                            <div>Requires Gemini Key</div>
                                                            <div>for audio transcription.</div>
                                                        </div>
                                                        <Tooltip.Arrow className={isLightTheme ? 'fill-white' : 'fill-[rgb(var(--surface-dark-rgb,15_23_42))]'} />
                                                    </Tooltip.Content>
                                                </Tooltip.Portal>
                                            )}
                                        </Tooltip.Root>
                                    </Tooltip.Provider>
                                    {isRecording && (<button onClick={cancelRecording} className={cancelRecordingButtonClasses} aria-label="Cancel recording"><Trash2 size={18} /></button>)}
                                    {canAttachFiles && !isRecording && (<button onClick={() => triggerFileUpload('file')} className="p-2 text-on-surface-dim hover:text-on-surface transition-colors disabled:opacity-50" aria-label="Attach file" disabled={hasUnreadyFiles || isNavigating || !currentConversationId}><Paperclip className="w-4 h-4" /></button>)}
                                    {canAttachImages && !isRecording && (<button onClick={() => triggerFileUpload('image')} className="p-2 text-on-surface-dim hover:text-on-surface transition-colors disabled:opacity-50" aria-label="Attach image" disabled={hasUnreadyFiles || isNavigating || !currentConversationId}><Image className="w-4 h-4" /></button>)}
                                </div>
                            </div>
                        </div>
                        <button onClick={handleSubmit} onDoubleClick={handleCancelSend} disabled={(!canSend && !isConversationProcessing) || isRecording} aria-label={isConversationProcessing ? "Processing..." : "Send"} className={sendButtonClasses}>
                            {isNavigating ? <OrbitalSpinner /> : hasUnreadyFiles ? <Loader className="w-4 h-4 animate-spin" /> : isConversationProcessing ? <OrbitalSpinner /> : <ArrowRight className="w-4 h-4" />}
                        </button>
                    </div>
                </div>
            </div>
        </>
    );
};

export default React.memo(InputArea);